import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { DonationContactField } from '@/components/donations/donation-contact-field'
import { DonationsList } from '@/components/donations/donations-list'
import {
  EditDonationDialog,
  type EditableDonationFacts,
} from '@/components/donations/edit-donation-dialog'
import {
  CampaignField,
  DonationTributeFields,
  type DonationTributeValues,
} from '@/components/donations/donation-form-fields'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { submitDonation } from '@/lib/donations'
import type { Contact } from '@/lib/contacts/types'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { useViewOnly } from '@/components/templates/app-shell'
import { Badge } from '@/components/ui/badge'

interface Donation {
  id: number
  gift_on: string
  donor: string
  donor_email?: string | null
  amount: string
  amount_cents?: number
  method: string
  payment_type?: string
  frequency_kind?: 'one_time' | 'recurring'
  campaign: string | null
  campaign_id?: number | null
  tribute_type?: 'honor' | 'memory' | null
  honoree_name?: string | null
  display_anonymous?: boolean
  donor_comment?: string | null
  staff_note?: string | null
  status?: string
  refunded?: boolean
  tribute?: string | null
  receipt_email_send_id?: number | null
}

interface Campaign {
  id: number
  name: string
}

interface DonationForm extends DonationTributeValues {
  contactId: string
  amount: string
  giftOn: string
  paymentType: 'cash' | 'check'
  checkNumber: string
  idempotencyKey: string
  campaignId: string
  sendReceipt: boolean
}

// This page coordinates the list, record dialog, edit dialog, and receipt mutation.
// eslint-disable-next-line complexity
export function DonationsPage({
  currentAssociationDate,
}: {
  currentAssociationDate?: string
  onOpenCampaign?: (campaignId: number) => void
}) {
  const queryClient = useQueryClient()
  const viewOnly = useViewOnly()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editingDonation, setEditingDonation] = React.useState<Donation | null>(null)
  const donations = useQuery({
    queryKey: ['donations'],
    queryFn: () => getJSON<{ donations: Donation[] }>('/api/v1/donations'),
    retry: false,
  })
  const contacts = useQuery({
    queryKey: ['contacts', 'donation-form'],
    queryFn: () =>
      getJSON<{ contacts: Contact[] }>('/api/v1/contacts?include_anonymous_donor=true'),
    enabled: dialogOpen || editingDonation !== null,
    retry: false,
  })
  const campaignOptions = useQuery({
    queryKey: ['campaigns', 'donation-form'],
    queryFn: () => getJSON<{ campaigns: Campaign[] }>('/api/v1/campaigns'),
    enabled: dialogOpen || editingDonation !== null,
    retry: false,
  })
  const updateDonation = useMutation({
    mutationFn: ({ id, facts }: { id: number; facts: EditableDonationFacts }) =>
      patchJSON<{ donation: Donation }>(`/api/v1/donations/${id}`, { donation: facts }),
    onSuccess: async () => {
      setEditingDonation(null)
      await queryClient.invalidateQueries({ queryKey: ['donations'] })
    },
  })
  const createDonation = useMutation({
    mutationFn: submitDonation,
    onSuccess: async () => {
      setDialogOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['donations'] })
    },
  })
  const resendReceipt = useMutation({
    mutationFn: (emailSendId: number) => postJSON(`/api/v1/email_sends/${emailSendId}/resend`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['donations'] }),
  })

  return (
    <>
      {viewOnly && <Badge variant="info">View only</Badge>}
      {!viewOnly && (
        <Button type="button" onClick={() => setDialogOpen(true)}>
          Record donation
        </Button>
      )}
      <DonationsList
        rows={donations.data?.donations ?? []}
        loading={donations.isLoading}
        error={donations.isError}
        onRowOpen={viewOnly ? undefined : setEditingDonation}
        onResendReceipt={viewOnly ? undefined : (id) => resendReceipt.mutate(id)}
      />
      {!viewOnly && dialogOpen && (
        <RecordDonationDialog
          contacts={contacts.data?.contacts ?? []}
          contactsLoading={contacts.isLoading}
          campaigns={campaignOptions.data?.campaigns ?? []}
          campaignsLoading={campaignOptions.isLoading}
          currentAssociationDate={currentAssociationDate}
          pending={createDonation.isPending}
          error={createDonation.error}
          onClose={() => setDialogOpen(false)}
          onSubmit={(form) => createDonation.mutate(form)}
        />
      )}
      {!viewOnly && (
        <DonationEditOverlay
          donation={editingDonation}
          campaigns={campaignOptions.data?.campaigns ?? []}
          campaignsLoading={campaignOptions.isLoading}
          pending={updateDonation.isPending}
          error={updateDonation.error}
          onClose={() => setEditingDonation(null)}
          onSubmit={(facts) => updateDonation.mutate({ id: editingDonation?.id ?? 0, facts })}
        />
      )}
    </>
  )
}

function DonationEditOverlay({
  donation,
  ...props
}: { donation: Donation | null } & Omit<
  React.ComponentProps<typeof EditDonationDialog>,
  'donation'
>) {
  if (!donation) return null
  return <EditDonationDialog donation={donation} {...props} />
}

function RecordDonationDialog({
  contacts,
  contactsLoading,
  campaigns,
  campaignsLoading,
  currentAssociationDate,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  contacts: Contact[]
  contactsLoading: boolean
  campaigns: Campaign[]
  campaignsLoading: boolean
  currentAssociationDate?: string
  pending: boolean
  error: Error | null
  onClose: () => void
  onSubmit: (form: DonationForm) => void
}) {
  const queryClient = useQueryClient()
  const [creatingContact, setCreatingContact] = React.useState(false)
  const [newContact, setNewContact] = React.useState({ name: '', email: '' })
  const [createdContact, setCreatedContact] = React.useState<Contact | null>(null)
  const [form, setForm] = React.useState<DonationForm>(() => ({
    contactId: '',
    amount: '',
    giftOn: currentAssociationDate ?? new Date().toISOString().slice(0, 10),
    paymentType: 'check',
    checkNumber: '',
    campaignId: '',
    idempotencyKey: crypto.randomUUID(),
    sendReceipt: true,
    tributeType: null,
    honoreeName: '',
    displayAnonymous: false,
  }))
  const createContact = useMutation({
    mutationFn: () => postJSON<{ person: Contact }>('/api/v1/persons', { person: newContact }),
    onSuccess: async ({ person }) => {
      setCreatedContact(person)
      setForm((current) => ({ ...current, contactId: String(person.id) }))
      setCreatingContact(false)
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
  const availableContacts = createdContact
    ? [...contacts.filter((contact) => contact.id !== createdContact.id), createdContact]
    : contacts

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          <DialogHeader>
            <DialogTitle>Record donation</DialogTitle>
          </DialogHeader>
          <DonationFormFields
            contacts={availableContacts}
            contactsLoading={contactsLoading}
            createdContact={createdContact}
            creatingContact={creatingContact}
            createContactError={createContact.error}
            createContactPending={createContact.isPending}
            error={error}
            form={form}
            campaigns={campaigns}
            campaignsLoading={campaignsLoading}
            newContact={newContact}
            onAddContact={() => createContact.mutate()}
            setCreatingContact={setCreatingContact}
            setForm={setForm}
            setNewContact={setNewContact}
          />
          <label
            htmlFor="donation-send-receipt"
            className="mt-3 flex items-center gap-2 text-body-sm"
          >
            <Checkbox
              id="donation-send-receipt"
              checked={form.sendReceipt}
              onCheckedChange={(checked) => setForm({ ...form, sendReceipt: checked === true })}
            />
            Email receipt to donor
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" className="h-8" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="h-8" disabled={pending || !form.contactId}>
              {pending ? 'Recording...' : 'Record donation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DonationFormFields({
  contacts,
  contactsLoading,
  createdContact,
  creatingContact,
  createContactError,
  createContactPending,
  error,
  form,
  newContact,
  onAddContact,
  setCreatingContact,
  setForm,
  setNewContact,
  campaigns,
  campaignsLoading,
}: {
  contacts: Contact[]
  contactsLoading: boolean
  createdContact: Contact | null
  creatingContact: boolean
  createContactError: Error | null
  createContactPending: boolean
  error: Error | null
  form: DonationForm
  newContact: { name: string; email: string }
  onAddContact: () => void
  setCreatingContact: React.Dispatch<React.SetStateAction<boolean>>
  setForm: React.Dispatch<React.SetStateAction<DonationForm>>
  setNewContact: React.Dispatch<React.SetStateAction<{ name: string; email: string }>>
  campaigns: Campaign[]
  campaignsLoading: boolean
}) {
  const fieldError = (field: string) => apiFieldMessages(error, field)[0]

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <DonationContactField
        contacts={contacts}
        contactsLoading={contactsLoading}
        createdContact={createdContact}
        creatingContact={creatingContact}
        createContactError={createContactError}
        createContactPending={createContactPending}
        error={fieldError('contact')}
        newContact={newContact}
        onAddContact={onAddContact}
        onSelectContact={(contactId) => setForm({ ...form, contactId })}
        selectedContactId={form.contactId}
        setCreatingContact={setCreatingContact}
        setNewContact={setNewContact}
      />
      <FormField label="Amount" htmlFor="donation-amount" error={fieldError('amount')}>
        <div className="relative">
          <span className="absolute left-3 top-1.5 text-body-sm text-muted-foreground">$</span>
          <Input
            id="donation-amount"
            className="h-8 pl-6 tabular-nums"
            value={form.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
          />
        </div>
      </FormField>
      <FormField label="Date received" htmlFor="donation-date" error={fieldError('gift_on')}>
        <Input
          id="donation-date"
          type="date"
          className="h-8"
          value={form.giftOn}
          onChange={(event) => setForm({ ...form, giftOn: event.target.value })}
        />
      </FormField>
      <FormField label="Method" error={fieldError('payment_type')}>
        <Select
          value={form.paymentType}
          onValueChange={(paymentType: 'cash' | 'check') =>
            setForm({
              ...form,
              paymentType,
              checkNumber: paymentType === 'cash' ? '' : form.checkNumber,
            })
          }
        >
          <SelectTrigger aria-label="Method" className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="check">Check</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      {form.paymentType === 'check' && (
        <FormField label="Check #" htmlFor="donation-check" error={fieldError('check_number')}>
          <Input
            id="donation-check"
            className="h-8"
            value={form.checkNumber}
            onChange={(event) => setForm({ ...form, checkNumber: event.target.value })}
          />
        </FormField>
      )}
      <CampaignField
        campaigns={campaigns}
        disabled={campaignsLoading}
        form={form}
        setForm={setForm}
      />
      <DonationTributeFields form={form} setForm={setForm} />
    </div>
  )
}
